import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaVeinteM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout
      const padding = 24;
      let y = padding;

      const usableW = Math.max(0, width - padding * 2);
      const interGap = 12;
      // Left: 60% for graphic, Right: 40% for texto
      const leftAreaW = Math.round(usableW * 0.6);
      const rightAreaW = Math.max(0, usableW - leftAreaW - interGap);
      const leftAreaX = padding;
      const rightAreaX = leftAreaX + leftAreaW + interGap;

      // column backgrounds
      ctx.fillStyle = opts.leftAreaBg || '#ffffff';
      ctx.fillRect(leftAreaX, 0, leftAreaW, height);
      ctx.fillStyle = opts.rightAreaBg || '#ffffff';
      ctx.fillRect(rightAreaX, 0, rightAreaW, height);

      // title block
      const titleFontSize = opts.titleFontSize || 50;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 14;
      const squareText = opts.titleSquareText || '20';
      const innerLeftPad = 8;
      const sqX = leftAreaX + innerLeftPad;

      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(sqX, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, sqX + squareSize / 2, y + squareSize / 2);

      const title = opts.title || props.title || '';
      const titleX = sqX + squareSize + squareGap;
      const titleMaxW = Math.max(0, leftAreaW - (squareSize + squareGap) - innerLeftPad - 12);

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      // wrap util with justification metadata
      const wrapLines = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });
        return lines;
      };

      const titleLines = wrapLines(ctx, title, titleMaxW);
      const titleLineH = Math.round(titleFontSize * 1.12);
      const spaceW = ctx.measureText(' ').width;
      for (let i = 0; i < titleLines.length; i++) {
        const ln = titleLines[i];
        const isLast = i === titleLines.length - 1;
        if (!isLast && ln.words.length > 1) {
          // justify
          const gaps = ln.words.length - 1;
          const extra = (titleMaxW - ln.wordsWidth) / gaps;
          let cx = titleX;
          for (let j = 0; j < ln.words.length; j++) {
            ctx.fillText(ln.words[j], cx, y + (i * titleLineH));
            cx += ctx.measureText(ln.words[j]).width + spaceW + extra;
          }
        } else {
          ctx.fillText(ln.words.join(' '), titleX, y + (i * titleLineH));
        }
      }
      y = Math.max(y + squareSize, y + titleLines.length * titleLineH) + 12;

      // subtitle
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 16;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLines = wrapLines(ctx, subTitle, titleMaxW);
        const subLineH = Math.round(subFontSize * 1.25);
        const subSpaceW = ctx.measureText(' ').width;
        for (let i = 0; i < subLines.length; i++) {
          const ln = subLines[i];
          const isLast = i === subLines.length - 1;
          if (!isLast && ln.words.length > 1) {
            // justify
            const gaps = ln.words.length - 1;
            const extra = (titleMaxW - ln.wordsWidth) / gaps;
            let cx = titleX;
            for (let j = 0; j < ln.words.length; j++) {
              ctx.fillText(ln.words[j], cx, y + (i * subLineH));
              cx += ctx.measureText(ln.words[j]).width + subSpaceW + extra;
            }
          } else {
            ctx.fillText(ln.words.join(' '), titleX, y + (i * subLineH));
          }
        }
        y += subLines.length * subLineH + 18;
      } else {
        y += 12;
      }

      // data and texto
      const data = Array.isArray(opts.data) ? opts.data : (Array.isArray(props.data) ? props.data : []);
      const texto = Array.isArray(opts.texto) ? opts.texto : (Array.isArray(props.texto) ? props.texto : []);

      // Prepare drawing area for vertical grouped bars (valor + porcentaje) inside left column
      const leftContentX = leftAreaX + innerLeftPad;
      const leftContentW = Math.max(0, leftAreaW - innerLeftPad * 2);

      const chartsTopY = y;
      const chartsBottom = Math.max(chartsTopY + 160, Math.round(height * 0.58)); // reasonable chart height area
      const labelAreaH = 48; // space under bars for labels
      const barAreaH = chartsBottom - chartsTopY - labelAreaH;
      const groupCount = Math.max(0, data.length);
      const groupGap = 20;
      const minGroupW = 60;
      const availableBarW = Math.max(40, leftContentW - 16);
      const groupWidth = groupCount > 0 ? Math.max(minGroupW, Math.floor((availableBarW - (groupGap * (groupCount + 1))) / groupCount)) : minGroupW;
      const innerBarGap = 8;
      const barWidth = Math.max(10, Math.floor((groupWidth - innerBarGap) / 2));
      const totalGroupsWidth = groupCount * groupWidth + (groupCount + 1) * groupGap;
      const groupsStartX = leftContentX + Math.round((leftContentW - totalGroupsWidth) / 2) + groupGap;

      // compute numeric values and max
      const parsed = data.map((r) => {
        const valor = Number(r?.valor ?? 0) || 0;
        // parse porcentaje: handle '21%', '21', or numeric
        let pctNum = 0;
        if (r?.porcentaje != null) {
          const s = String(r.porcentaje).replace(/\s/g, '');
          const m = s.match(/-?[\d,.]+/);
          pctNum = m ? Number(m[0].replace(',', '.')) : 0;
        }
        const categoria = r?.categoria ?? r?.Categoria ?? '';
        return { ...r, valor, pctNum, porcentaje: r?.porcentaje ?? '', categoria };
      });

      const maxValor = Math.max(1, parsed.reduce((m, r) => Math.max(m, r.valor), 0));
      const maxPct = Math.max(100, parsed.reduce((m, r) => Math.max(m, r.pctNum), 0));

      // palette: index 0 -> valor, index 1 -> porcentaje
      const palette = opts.palette || ['#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#6366f1', '#8b5cf6'];

      // draw horizontal grid lines for valor (left axis) and pct (right axis labels)
      ctx.strokeStyle = opts.gridColor || 'rgba(0,0,0,0.06)';
      ctx.lineWidth = 1;

      // left axis (valor) ticks and grid
      ctx.font = `${Math.max(10, Math.round((opts.axisLabelSize || 11)))}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.axisLabelColor || '#ffffff';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const leftSteps = 4;
      for (let s = 0; s <= leftSteps; s++) {
        const gy = chartsTopY + Math.round((barAreaH * (1 - s / leftSteps)));
        const val = Math.round((maxValor * (s / leftSteps)) * 100) / 100;
        ctx.beginPath();
        ctx.moveTo(leftContentX + 8, gy);
        ctx.lineTo(leftContentX + leftContentW - 8, gy);
        ctx.stroke();
        ctx.fillText(String(val), leftContentX + 8 - 6, gy);
      }

      // right axis (percentage) labels (draw near leftContentW right edge)
      ctx.textAlign = 'left';
      const rightSteps = 4;
      for (let s = 0; s <= rightSteps; s++) {
        const gy = chartsTopY + Math.round((barAreaH * (1 - s / rightSteps)));
        const pval = Math.round((maxPct * (s / rightSteps)) * 100) / 100;
        ctx.fillText(String(pval) + '%', leftContentX + leftContentW - 8 + 6, gy);
      }

      // draw grouped bars (left column)
      let gx = groupsStartX;
      ctx.textBaseline = 'bottom';
      for (let i = 0; i < parsed.length; i++) {
        const row = parsed[i];
        const colorValor = palette[0];
        const colorPct = palette[1];

        // valor bar
        const valorH = maxValor > 0 ? Math.round((row.valor / maxValor) * barAreaH) : 0;
        const valorBy = chartsTopY + (barAreaH - valorH);
        const valorX = gx;
        // percentage bar
        const pctH = maxPct > 0 ? Math.round((row.pctNum / maxPct) * barAreaH) : 0;
        const pctBy = chartsTopY + (barAreaH - pctH);
        const pctX = gx + barWidth + innerBarGap;

        // bar backgrounds (group background subtle)
        ctx.fillStyle = opts.barBg || '#ffffff';
        ctx.fillRect(gx, chartsTopY, groupWidth, barAreaH);

        // draw valor bar
        ctx.fillStyle = colorValor;
        ctx.fillRect(valorX, valorBy, barWidth, valorH);

        // draw pct bar
        ctx.fillStyle = colorPct;
        ctx.fillRect(pctX, pctBy, barWidth, pctH);

        // value text above valor bar
        ctx.font = `${Math.max(10, Math.round((opts.valueFontSize || 11)))}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.valueColor || '#0f172a';
        ctx.textAlign = 'center';
        const valorText = String(row.valor ?? '');
        ctx.fillText(valorText, valorX + Math.round(barWidth / 2), valorBy - 6);

        // pct text above pct bar
        const pctText = row.porcentaje ? String(row.porcentaje) : (row.pctNum ? `${row.pctNum}%` : '');
        ctx.fillText(pctText, pctX + Math.round(barWidth / 2), pctBy - 6);

        // category label below group - justified within group width
        ctx.font = `bold ${Math.max(10, Math.round((opts.labelSize || 11)))}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.labelColor || '#111827';
        const catLines = wrapLines(ctx, String(row.categoria || ''), Math.max(24, groupWidth));
        const catSpaceW = ctx.measureText(' ').width;
        const labelBaseY = chartsBottom - Math.round(labelAreaH / 2) + (catLines.length === 2 ? -6 : 0);
        if (catLines.length === 1) {
          const ln = catLines[0];
          ctx.textBaseline = 'middle';
          const centerX = gx + Math.round(groupWidth / 2);
          if (ln.words.length > 1) {
            // justify single line within groupWidth
            const gaps = ln.words.length - 1;
            const extra = (Math.max(24, groupWidth) - ln.wordsWidth) / gaps;
            let cx = gx + Math.round((groupWidth - Math.max(24, groupWidth)) / 2);
            ctx.textAlign = 'left';
            for (let j = 0; j < ln.words.length; j++) {
              ctx.fillText(ln.words[j], cx, labelBaseY);
              cx += ctx.measureText(ln.words[j]).width + catSpaceW + extra;
            }
          } else {
            ctx.textAlign = 'center';
            ctx.fillText(ln.words.join(' '), centerX, labelBaseY);
          }
        } else {
          const centerX = gx + Math.round(groupWidth / 2);
          // first line
          const ln0 = catLines[0];
          ctx.textBaseline = 'bottom';
          if (ln0.words.length > 1) {
            const gaps = ln0.words.length - 1;
            const extra = (Math.max(24, groupWidth) - ln0.wordsWidth) / gaps;
            let cx = gx + Math.round((groupWidth - Math.max(24, groupWidth)) / 2);
            ctx.textAlign = 'left';
            for (let j = 0; j < ln0.words.length; j++) {
              ctx.fillText(ln0.words[j], cx, labelBaseY - 2);
              cx += ctx.measureText(ln0.words[j]).width + catSpaceW + extra;
            }
          } else {
            ctx.textAlign = 'center';
            ctx.fillText(ln0.words.join(' '), centerX, labelBaseY - 2);
          }
          // second line (last line, don't justify)
          const ln1 = catLines[1] || { words: [''], wordsWidth: 0 };
          ctx.textBaseline = 'top';
          ctx.textAlign = 'center';
          ctx.fillText(ln1.words.join(' '), centerX, labelBaseY + 8);
        }

        gx += groupWidth + groupGap;
      }

      // draw a small key for bars (valor / porcentaje) under the chart area (left column)
      const keyY = chartsBottom + 6;
      const keyX = leftContentX + 8;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = `${Math.max(10, Math.round((opts.itemTextSize || 12)))}px Inter, Arial, sans-serif`;
      // valor key
      ctx.fillStyle = palette[0];
      ctx.fillRect(keyX, keyY, 12, 12);
      ctx.fillStyle = opts.itemTextColor || '#374151';
      ctx.fillText('Valor', keyX + 18, keyY + 6);
      // pct key
      const key2X = keyX + 110;
      ctx.fillStyle = palette[1];
      ctx.fillRect(key2X, keyY, 12, 12);
      ctx.fillStyle = opts.itemTextColor || '#374151';
      ctx.fillText('Porcentaje', key2X + 18, keyY + 6);

      // Draw texto paragraphs in the RIGHT column (top-aligned, wrapped)
      const rightInnerPad = 12;
      const rightContentX = rightAreaX + rightInnerPad;
      const rightContentW = Math.max(40, rightAreaW - rightInnerPad * 2);
      let ry = chartsTopY;

      // Set right-column texto font size to 40px
      ctx.font = `40px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.itemTextColor || '#374151';
      const rightLineH = Math.round(40 * 1.4);
      const textoSpaceW = ctx.measureText(' ').width;

      for (let pIdx = 0; pIdx < texto.length; pIdx++) {
        const p = texto[pIdx];
        const lines = wrapLines(ctx, String(p || ''), rightContentW);
        for (let lIdx = 0; lIdx < lines.length; lIdx++) {
          const ln = lines[lIdx];
          const isLastOfParagraph = lIdx === lines.length - 1;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          if (!isLastOfParagraph && ln.words.length > 1) {
            // justify
            const gaps = ln.words.length - 1;
            const extra = (rightContentW - ln.wordsWidth) / gaps;
            let cx = rightContentX;
            for (let j = 0; j < ln.words.length; j++) {
              ctx.fillText(ln.words[j], cx, ry);
              cx += ctx.measureText(ln.words[j]).width + textoSpaceW + extra;
            }
          } else {
            ctx.fillText(ln.words.join(' '), rightContentX, ry);
          }
          ry += rightLineH;
        }
        ry += 8;
      }

      // (Optional) If developer passed a `textoRight` prop keep legacy centered behavior under any remaining space
      const textoRightRaw = opts.textoRight ?? opts.rightText ?? props.textoRight ?? props.rightText ?? '';
      const legacyRightParagraphs = Array.isArray(textoRightRaw) ? textoRightRaw : (textoRightRaw ? [String(textoRightRaw)] : []);
      if (legacyRightParagraphs.length) {
        // justify them in the remaining vertical space of right column
        ctx.save();
        let rightFontSize = typeof opts.textoRightSize === 'number' ? opts.textoRightSize : 50;
        const minFontSize = 8;
        ctx.fillStyle = opts.textoRightColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const maxRightW = Math.max(8, rightAreaW - 24);
        const topBound = chartsTopY;
        const bottomBound = chartsBottom;
        let paragraphLines = [];
        let lineH = 0;
        let totalH = 0;
        while (rightFontSize >= minFontSize) {
          ctx.font = `${rightFontSize}px Inter, Arial, sans-serif`;
          paragraphLines = [];
          for (let p of legacyRightParagraphs) {
            const wrapped = wrapLines(ctx, p, maxRightW);
            if (!wrapped.length) wrapped.push({ words: [''], wordsWidth: 0 });
            paragraphLines.push(wrapped);
          }
          const totalLines = paragraphLines.reduce((sum, para, idx) => sum + para.length + (idx < paragraphLines.length - 1 ? 1 : 0), 0);
          lineH = Math.round(rightFontSize * 1.3);
          totalH = totalLines * lineH;
          if (totalH <= (bottomBound - topBound) || rightFontSize === minFontSize) break;
          rightFontSize -= 1;
        }
        const startY = topBound + Math.round(((bottomBound - topBound) - totalH) / 2) + Math.round(lineH / 2);
        const leftX = rightAreaX + 12;
        const legacySpaceW = ctx.measureText(' ').width;
        ctx.fillStyle = opts.textoRightColor || '#374151';
        let currentY = startY;
        for (let pIdx = 0; pIdx < paragraphLines.length; pIdx++) {
          const para = paragraphLines[pIdx];
          for (let lIdx = 0; lIdx < para.length; lIdx++) {
            const ln = para[lIdx];
            const isLastLineOfParagraph = lIdx === para.length - 1;
            if (!isLastLineOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (maxRightW - ln.wordsWidth) / gaps;
              let cx = leftX;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, currentY);
                cx += ctx.measureText(ln.words[j]).width + legacySpaceW + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), leftX, currentY);
            }
            currentY += lineH;
          }
          if (pIdx < paragraphLines.length - 1) currentY += lineH;
        }
        ctx.restore();
      }

      // footer / logo
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_veinte_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});